import { useCallback, useEffect, useRef, useState } from 'react';
import { getConvertedTimeToUTC, getTimeZone, stringFormatting } from "./utils";
import Helper from "./Helper";
import { useEventTypes } from "../context/EventTypeContext";
import EventType from "../types/instance/EventType";
import { CHART_COLOURS_WAITS, OTHER_COLOUR_CODE, TARGET_COLOUR_CODES } from "../component/Constants";
import { InstanceOptions } from "../container/comparison/activity-comparison/types";
import dayjs, { Dayjs } from "dayjs";
import CombinedChanges from "../types/instance/CombinedChanges";
import ChartPlotLine from "../types/ChartPlotLine";
import { EChartPoint, EChartTimeSeries } from "../component/chart/echarts/types";
import { TimeSeriesChartProps } from "../component/chart/echarts/TimeSeriesChart";

const useChartOptions = (chartData: any, instanceOptions: InstanceOptions[], options: any, generateId: number, setStatementColors?: Function, setWaitsColors?: Function) => {

    const [chartOptions, setChartOptions] = useState<(TimeSeriesChartProps | null)[]>([null, null]);
    const { eventTypes } = useEventTypes();

    const metric = options.metric
    let peakTimeSeries1 = _getPeekTime(chartData[0], metric);
    let peakTimeSeries2 = _getPeekTime(chartData[1], metric);

    // Use `useRef` to maintain a stable reference for statementColors
    const statementColors = useRef<{ [key: string]: string }>({});
    const waitsColors = useRef<{ [key: string]: string }>({});

    const getColourForStatement = useCallback((key: string) => {

        if (options.type === 'waits') {

            if (!waitsColors.current[key]) {
                const colorIndex = Object.keys(waitsColors.current).length % CHART_COLOURS_WAITS.length;
                waitsColors.current[key] = CHART_COLOURS_WAITS[colorIndex];

                // Update setStatementColors if it's provided
                if (setWaitsColors) {
                    setWaitsColors(waitsColors.current);
                }
            }
            return waitsColors.current[key];

        } else {
            if (!statementColors.current[key]) {
                const colorIndex = Object.keys(statementColors.current).length % TARGET_COLOUR_CODES.length;
                statementColors.current[key] = TARGET_COLOUR_CODES[colorIndex];

                // Update setStatementColors if it's provided
                if (setStatementColors) {
                    setStatementColors(statementColors.current);
                }
            }
            return statementColors.current[key];
        }

    }, [setStatementColors]);

    const chartType: EChartTimeSeries['chartType'] =
        options.chart === 'column' || options.chart === 'bar' ? 'bar'
            : options.chart === 'area' ? 'area'
                : 'line';

    const getConvertedData = (list: { values: [] }, metric: string, interval: number, start: number, end: number) => {
        const fullRange = [];
        for (let time = start; time <= end; time += interval) {
            fullRange.push(time);
        }

        const valueMap = new Map(list.values.map(item => [Math.floor(getConvertedTimeToUTC(item) / interval) * interval, item]));

        // Fill with values from `valueMap` or 0 for missing data points
        return fullRange.map((timestamp) => {
            const roundedTimestamp = Math.floor(timestamp / interval) * interval;
            const point = valueMap.get(roundedTimestamp);
            return point ? [timestamp, point[metric]] : [timestamp, 0]; // Use 0 for missing data points
        });
    };

    const getSeries = (data: any, index: number): EChartTimeSeries[] => {
        // If `data` is empty, create a single series with 0 values across the time range
        if (!Object.keys(data).length) {
            return [{
                name: 'No Data Available',
                chartType,
                data: getConvertedData(
                    { values: [] },
                    metric,
                    options.interval || 3600000,
                    instanceOptions[index].periodStart.valueOf(),
                    instanceOptions[index].periodEnd.valueOf()
                ) as EChartPoint[],
            }];
        }

        return Object.keys(data).map((key: string) => ({
            name: key,
            chartType,
            data: getConvertedData(
                chartData[index][key],
                metric,
                options.interval || 3600000,
                instanceOptions[index].periodStart.valueOf(),
                instanceOptions[index].periodEnd.valueOf()
            ) as EChartPoint[],
            color: key === 'Other SQL' ? OTHER_COLOUR_CODE : getColourForStatement(key),
        }));
    };

    useEffect(() => {
        if (Object.keys(chartData).length !== instanceOptions.length) {
            return;
        }

        const optionsToSet: TimeSeriesChartProps[] = [];

        instanceOptions.forEach((_, index: number) => {
            let plotLines: any[] = []
            if (options?.combinedChanges?.length) {
                const tz = getTimeZone();
                const periodFrom = tz === 'UTC' ? dayjs.utc(instanceOptions[index].periodStart) : dayjs(instanceOptions[index].periodStart);
                const periodTo = tz === 'UTC' ? dayjs.utc(instanceOptions[index].periodEnd) : dayjs(instanceOptions[index].periodEnd);
                // Index 0 is always the previous period's instance, index 1 the current period's -
                // used so a marker's "See Events" link opens the matching tab in the events history.
                const reportTab = index === 0 ? 'previous' : 'current';
                plotLines = _getPlotBandAndPlotLines(options.combinedChanges[index], periodFrom, periodTo, options.interval, eventTypes, reportTab)
                if (plotLines.length) {
                    plotLines = plotLines.map((chartPlotLine: ChartPlotLine) => {
                        return {
                            ...chartPlotLine,
                            value: chartPlotLine.timeValue
                        }
                    })
                }
            }

            const isTimeMetric = metric.includes('time') || metric === 'duration';

            optionsToSet.push({
                series: getSeries(chartData[index], index),
                tooltipType: options.tooltipType,
                plotLines: plotLines,
                legend: true,
                brush: false,
                height: options.height,
                xExtent: {
                    min: instanceOptions[index].periodStart.valueOf(),
                    max: instanceOptions[index].periodEnd.valueOf(),
                },
                yAxis: [{
                    labels: true,
                    name: `${stringFormatting(metric).replace(/_/gi, ' ')}`,
                    format: isTimeMetric ? 'time' : 'number',
                    max: Math.max(peakTimeSeries1, peakTimeSeries2),
                    position: index === 1 ? 'right' : 'left',
                }],
            });
        });
        setChartOptions(optionsToSet);
    }, [chartData, generateId]);

    return chartOptions;
};

const _getPlotBandAndPlotLines = (combinedChanges: [], from: Dayjs, to: Dayjs, interval: number, eventTypes: EventType[] = [], reportTab?: string) => {
    const chartCategories = Helper.getChartAxisCategories(from, to, interval)
    const formatChanges = transformData(combinedChanges)

    // No need for period(first param) since the 'see more' only expand / collapse section
    return Helper.getChartPlotLines(null, formatChanges, chartCategories, interval, true, undefined, eventTypes, reportTab);
}

function transformData(originalData: any) {
    let combinedChanges: CombinedChanges[] = []

    for (let index = 0; index < originalData.length; index++) {
        const currentItem = originalData[index];

        combinedChanges.push({
            id: index,
            instanceId: currentItem.datasourceid,
            timeslice: currentItem.timeslice,
            [currentItem.event]: currentItem
        });
    }
    return combinedChanges
}

const _getPeekTime = (data: any, metric: string) => {
    if (!data || !Object.keys(data).length) {
        return 0;
    }

    const timesliceMap: Record<string, number> = {};

    Object.values(data).forEach((item: any) => {
        if (item.values) {
            item.values.forEach((value: any) => {
                const { timeslice } = value;

                // Dynamically access the metric value
                const metricValue = value[metric];

                if (metricValue !== undefined) {
                    // Aggregate waittimes for each timeslice
                    if (timesliceMap[timeslice]) {
                        timesliceMap[timeslice] += metricValue;
                    } else {
                        timesliceMap[timeslice] = metricValue;
                    }
                }
            });
        }
    });

    let max = 0;
    for (const aggregatedWaittime of Object.values(timesliceMap)) {
        if (aggregatedWaittime > max) {
            max = aggregatedWaittime;
        }
    }
    return max;
};

export default useChartOptions;
